<script setup lang="ts">
import {
  PhArrowSquareOut,
  PhCaretRight,
  PhCheck,
  PhDownloadSimple,
  PhGlobeHemisphereWest,
  PhInfo,
  PhPalette,
  PhPlayCircle,
  PhSignIn,
  PhSignOut,
} from '@phosphor-icons/vue'
import { storeToRefs } from 'pinia'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemIndicator,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from 'reka-ui'
import { computed, shallowRef } from 'vue'
import type { Component } from 'vue'
import { user } from '@ideaflow/ui/classes/user'
import DreamaLoginLogo from '@/components/dreama-login-logo'
import { DEFAULT_APP_ROUTE } from '@/constants/routes'
import { getDreamaWebAppUrl } from '@/constants/domain'
import { useFeedPreferencesStore, type DiscoverAutoplayPreference } from '@/stores/feedPreferences'
import { useThemeStore, type ThemePreference } from '@/stores/theme'
import { useUserStore, type UserInfo } from '@/stores/user'
import { loginWithBlankClose } from '@/utils/login'
import type { Props } from './type'

defineOptions({
  name: 'AppSidebarMoreMenu',
})

defineProps<Props>()

interface ExternalMenuItem {
  key: string
  label: string
  href: string
  icon: Component
  openInNewTab?: boolean
}

interface ThemeOption {
  label: string
  value: ThemePreference
}

interface DiscoverAutoplayOption {
  label: string
  value: DiscoverAutoplayPreference
}

const EXTERNAL_ITEMS: ExternalMenuItem[] = [
  {
    key: 'download',
    label: '下载APP',
    href: '/app',
    icon: PhDownloadSimple,
    openInNewTab: true,
  },
  {
    key: 'website',
    label: '官网',
    href: '/',
    icon: PhGlobeHemisphereWest,
  },
  {
    key: 'about',
    label: '关于',
    href: '/about?20260510',
    icon: PhInfo,
    openInNewTab: true,
  },
]

const THEME_OPTIONS: ThemeOption[] = [
  { label: '深色', value: 'dark' },
  { label: '浅色', value: 'light' },
  { label: '跟随系统', value: 'system' },
]
const DISCOVER_AUTOPLAY_OPTIONS: DiscoverAutoplayOption[] = [
  { label: '自动播放开', value: 'on' },
  { label: '自动播放关', value: 'off' },
]
const LOGOUT_REDIRECT_URL = getDreamaWebAppUrl(DEFAULT_APP_ROUTE)

const themeStore = useThemeStore()
const feedPreferencesStore = useFeedPreferencesStore()
const userStore = useUserStore()
const { themePreference } = storeToRefs(themeStore)
const { discoverAutoplay } = storeToRefs(feedPreferencesStore)
const { userInfo } = storeToRefs(userStore)
const isOpen = shallowRef(false)
const openedWithPointer = shallowRef(false)
const suppressInitialHover = shallowRef(false)
const interactionMode = shallowRef<'pointer' | 'keyboard'>('pointer')
const themePreferenceLabelMap: Record<ThemePreference, string> = {
  dark: '深色',
  light: '浅色',
  system: '跟随系统',
}
const discoverAutoplayLabelMap: Record<DiscoverAutoplayPreference, string> = {
  on: '自动播放开',
  off: '自动播放关',
}
const isLoggedIn = computed(() => Boolean(userInfo.value?.token))

function handleOpenChange(open: boolean) {
  isOpen.value = open
  suppressInitialHover.value = open && openedWithPointer.value
  interactionMode.value = openedWithPointer.value ? 'pointer' : 'keyboard'

  if (!open) {
    openedWithPointer.value = false
  }
}

function handleTriggerPointerDown() {
  openedWithPointer.value = true
  interactionMode.value = 'pointer'
}

function handleTriggerKeydown() {
  openedWithPointer.value = false
  interactionMode.value = 'keyboard'
}

function releaseInitialHoverSuppression() {
  suppressInitialHover.value = false
}

function handleContentPointerMove() {
  interactionMode.value = 'pointer'
  releaseInitialHoverSuppression()
}

function handleContentKeydown() {
  interactionMode.value = 'keyboard'
  releaseInitialHoverSuppression()
}

function openMenuLink(item: ExternalMenuItem) {
  const targetUrl = item.href.startsWith('http')
    ? item.href
    : new URL(item.href, window.location.origin).toString()

  if (item.openInNewTab) {
    window.open(targetUrl, '_blank', 'noopener,noreferrer')
    return
  }

  window.location.assign(targetUrl)
}

function handleThemeChange(value: unknown) {
  if (value === 'dark' || value === 'light' || value === 'system') {
    themeStore.setTheme(value)
  }
}

function handleDiscoverAutoplayChange(value: unknown) {
  if (value === 'on' || value === 'off') {
    feedPreferencesStore.setDiscoverAutoplay(value)
  }
}

function syncUserFromCookie() {
  const cookieUser = user.get()

  if (!cookieUser) {
    return false
  }

  userStore.setUserInfo(cookieUser as unknown as UserInfo)
  return true
}

async function handleLogin() {
  const nextUserInfo = await loginWithBlankClose({
    logo: DreamaLoginLogo,
  })

  if (!nextUserInfo && !syncUserFromCookie()) {
    return
  }

  if (!syncUserFromCookie()) {
    userStore.setUserInfo(nextUserInfo as unknown as UserInfo)
  }

  await Promise.all([userStore.fetchMediaWhiteUids(), userStore.fetchIdentityByUserIds()])
  isOpen.value = false
}

async function handleLogout() {
  isOpen.value = false
  userStore.resetUserInfo()
  void userStore.performLogout()
  window.location.replace(LOGOUT_REDIRECT_URL)
}
</script>

<template>
  <DropdownMenuRoot :open="isOpen" @update:open="handleOpenChange">
    <DropdownMenuTrigger as-child>
      <button
        type="button"
        class="app-sidebar-more-menu__trigger"
        @pointerdown="handleTriggerPointerDown"
        @keydown="handleTriggerKeydown"
      >
        <slot />
      </button>
    </DropdownMenuTrigger>

    <DropdownMenuPortal>
      <DropdownMenuContent
        :side-offset="8"
        :align-offset="12"
        align="start"
        side="top"
        class="app-sidebar-more-menu__content"
        :data-suppress-initial-hover="suppressInitialHover ? '' : undefined"
        :data-interaction-mode="interactionMode"
        @pointermove="handleContentPointerMove"
        @keydown="handleContentKeydown"
      >
        <DropdownMenuItem
          v-for="item in EXTERNAL_ITEMS"
          :key="item.key"
          class="app-sidebar-more-menu__item"
          :class="{ 'app-sidebar-more-menu__item--external': item.openInNewTab }"
          @select="openMenuLink(item)"
        >
          <span class="app-sidebar-more-menu__item-main">
            <component :is="item.icon" :size="18" weight="regular" />
            <span>{{ item.label }}</span>
          </span>
          <PhArrowSquareOut
            v-if="item.openInNewTab"
            class="app-sidebar-more-menu__external-indicator"
            :size="14"
          />
        </DropdownMenuItem>

        <DropdownMenuSeparator class="app-sidebar-more-menu__separator" />

        <DropdownMenuSub>
          <DropdownMenuSubTrigger class="app-sidebar-more-menu__item">
            <span class="app-sidebar-more-menu__item-main">
              <PhPalette :size="18" weight="regular" />
              <span>外观</span>
            </span>
            <span class="app-sidebar-more-menu__item-trailing">
              <span class="app-sidebar-more-menu__meta">{{
                themePreferenceLabelMap[themePreference]
              }}</span>
              <PhCaretRight :size="14" weight="bold" />
            </span>
          </DropdownMenuSubTrigger>

          <DropdownMenuPortal>
            <DropdownMenuSubContent
              :side-offset="8"
              class="app-sidebar-more-menu__content app-sidebar-more-menu__content--sub"
            >
              <DropdownMenuRadioGroup
                :model-value="themePreference"
                @update:model-value="handleThemeChange"
              >
                <DropdownMenuRadioItem
                  v-for="option in THEME_OPTIONS"
                  :key="option.value"
                  :value="option.value"
                  class="app-sidebar-more-menu__item"
                >
                  <span class="app-sidebar-more-menu__item-main">
                    <span>{{ option.label }}</span>
                  </span>
                  <DropdownMenuItemIndicator class="app-sidebar-more-menu__item-indicator">
                    <PhCheck :size="14" weight="bold" />
                  </DropdownMenuItemIndicator>
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger class="app-sidebar-more-menu__item">
            <span class="app-sidebar-more-menu__item-main">
              <PhPlayCircle :size="18" weight="regular" />
              <span>发现内容</span>
            </span>
            <span class="app-sidebar-more-menu__item-trailing">
              <span class="app-sidebar-more-menu__meta">{{
                discoverAutoplayLabelMap[discoverAutoplay]
              }}</span>
              <PhCaretRight :size="14" weight="bold" />
            </span>
          </DropdownMenuSubTrigger>

          <DropdownMenuPortal>
            <DropdownMenuSubContent
              :side-offset="8"
              class="app-sidebar-more-menu__content app-sidebar-more-menu__content--sub"
            >
              <DropdownMenuRadioGroup
                :model-value="discoverAutoplay"
                @update:model-value="handleDiscoverAutoplayChange"
              >
                <DropdownMenuRadioItem
                  v-for="option in DISCOVER_AUTOPLAY_OPTIONS"
                  :key="option.value"
                  :value="option.value"
                  class="app-sidebar-more-menu__item"
                >
                  <span class="app-sidebar-more-menu__item-main">
                    <span>{{ option.label }}</span>
                  </span>
                  <DropdownMenuItemIndicator class="app-sidebar-more-menu__item-indicator">
                    <PhCheck :size="14" weight="bold" />
                  </DropdownMenuItemIndicator>
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        <DropdownMenuItem
          class="app-sidebar-more-menu__item"
          @select="isLoggedIn ? handleLogout() : handleLogin()"
        >
          <span class="app-sidebar-more-menu__item-main">
            <component :is="isLoggedIn ? PhSignOut : PhSignIn" :size="18" weight="regular" />
            <span>{{ isLoggedIn ? '退出账号' : '登录' }}</span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>

<style scoped>
.app-sidebar-more-menu__trigger {
  display: flex;
  width: 100%;
  padding: 0;
  border: 0;
  border-radius: inherit;
  appearance: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
}
</style>

<style>
.app-sidebar-more-menu__content {
  z-index: 40;
  min-width: 236px;
  width: 236px;
  padding: var(--app-dropdown-padding);
  border: 1px solid var(--border-dropdown, var(--app-border-strong));
  border-radius: var(--app-dropdown-radius);
  background-color: var(--bg-dropdown, var(--app-surface-raised));
  background-image: none;
  box-shadow: var(--app-shadow-floating);
  overflow: hidden;
  opacity: 1;
  transform-origin: var(--reka-dropdown-menu-content-transform-origin);
  animation: app-dropdown-enter var(--app-dropdown-motion-duration) var(--app-dropdown-motion-ease);
}

.app-sidebar-more-menu__content--sub {
  min-width: 220px;
  width: 220px;
  background-color: var(--bg-dropdown, var(--app-surface-raised));
  background-image: none;
}

.app-sidebar-more-menu__item {
  display: flex;
  min-height: var(--app-dropdown-item-min-height);
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: var(--app-dropdown-item-padding-y) var(--app-dropdown-item-padding-x);
  border-radius: var(--app-dropdown-item-radius);
  color: var(--app-text-main);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  cursor: pointer;
  outline: none;
  transition:
    background-color var(--app-dropdown-motion-duration) ease,
    color var(--app-dropdown-motion-duration) ease;
}

.app-sidebar-more-menu__content:not([data-suppress-initial-hover])
  .app-sidebar-more-menu__item:hover {
  background-color: var(--app-state-layer-hover);
}

.app-sidebar-more-menu__content[data-interaction-mode='keyboard']:not([data-suppress-initial-hover])
  .app-sidebar-more-menu__item[data-highlighted] {
  background-color: var(--app-state-layer-hover);
}

.app-sidebar-more-menu__content:not([data-suppress-initial-hover])
  .app-sidebar-more-menu__item:active {
  background-color: var(--app-state-layer-pressed);
}

.app-sidebar-more-menu__item-main,
.app-sidebar-more-menu__item-trailing {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  white-space: nowrap;
}

.app-sidebar-more-menu__item-main {
  gap: 10px;
}

.app-sidebar-more-menu__item-trailing {
  flex: 0 0 auto;
  gap: 4px;
}

.app-sidebar-more-menu__item-main > :first-child {
  flex: 0 0 auto;
  color: var(--app-text-main);
}

.app-sidebar-more-menu__item-trailing {
  color: var(--app-text-soft);
}

.app-sidebar-more-menu__meta {
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.app-sidebar-more-menu__separator {
  height: 1px;
  margin: 6px 4px;
  background: var(--app-border-subtle);
}

.app-sidebar-more-menu__external-indicator {
  color: var(--app-text-soft);
  opacity: 0;
  transform: translateX(-2px);
  transition:
    opacity var(--app-dropdown-motion-duration) ease,
    transform var(--app-dropdown-motion-duration) ease;
}

.app-sidebar-more-menu__content:not([data-suppress-initial-hover])
  .app-sidebar-more-menu__item--external:hover
  .app-sidebar-more-menu__external-indicator {
  opacity: 1;
  transform: translateX(0);
}

.app-sidebar-more-menu__content[data-interaction-mode='keyboard']:not([data-suppress-initial-hover])
  .app-sidebar-more-menu__item--external[data-highlighted]
  .app-sidebar-more-menu__external-indicator {
  opacity: 1;
  transform: translateX(0);
}

.app-sidebar-more-menu__item-indicator {
  display: inline-flex;
  align-items: center;
  color: var(--app-text-main);
}

@keyframes app-dropdown-enter {
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
